import { useState, useCallback } from 'react';
import {
  uploadImageToCloudinary,
  uploadProductImages,
  uploadImagesWithProgress,
  validateFile,
  validateCloudinaryConfig,
  getSupplierFolderStructure,
  CloudinaryUploadResult,
  SupplierInfo,
  ProductInfo,
  UploadOptions,
} from '@/lib/cloudinary-upload-service';

export interface UseCloudinaryUploadOptions {
  onProgress?: (progress: number, completed: number, total: number, currentFile?: string) => void;
  onSuccess?: (results: CloudinaryUploadResult[]) => void;
  onError?: (error: string) => void;
  onFileValidationError?: (file: File, error: string) => void;
  uploadOptions?: UploadOptions;
}

export interface UseCloudinaryUploadReturn {
  uploadSingleImage: (file: File, supplier: SupplierInfo, product?: ProductInfo) => Promise<CloudinaryUploadResult>;
  uploadMultipleImages: (files: File[], supplier: SupplierInfo, product?: ProductInfo) => Promise<CloudinaryUploadResult[]>;
  uploadWithProgress: (files: File[], supplier: SupplierInfo, product?: ProductInfo) => Promise<CloudinaryUploadResult[]>;
  validateFiles: (files: File[]) => { valid: File[]; invalid: Array<{ file: File; error: string }> };
  getPreviewStructure: (supplier: SupplierInfo, product?: ProductInfo) => { supplierFolder: string; fullPath: string; exampleUrl: string };
  isUploading: boolean;
  uploadProgress: number;
  uploadedCount: number;
  totalCount: number;
  currentFile: string | null;
  error: string | null;
  results: CloudinaryUploadResult[];
  configValid: boolean;
  configErrors: string[];
  resetState: () => void;
}

export function useCloudinaryUpload(options: UseCloudinaryUploadOptions = {}): UseCloudinaryUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [currentFile, setCurrentFile] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<CloudinaryUploadResult[]>([]);

  // Validate Cloudinary configuration on hook initialization
  const configValidation = validateCloudinaryConfig();
  const [configValid] = useState(configValidation.isValid);
  const [configErrors] = useState(configValidation.errors);

  const resetState = useCallback(() => {
    setIsUploading(false);
    setUploadProgress(0);
    setUploadedCount(0);
    setTotalCount(0);
    setCurrentFile(null);
    setError(null);
    setResults([]);
  }, []);

  const validateFiles = useCallback((files: File[]) => {
    const valid: File[] = [];
    const invalid: Array<{ file: File; error: string }> = [];

    files.forEach(file => {
      const validation = validateFile(file);
      if (validation.isValid) {
        valid.push(file);
      } else {
        invalid.push({ file, error: validation.error || 'Unknown validation error' });
        options.onFileValidationError?.(file, validation.error || 'Unknown validation error');
      }
    });

    return { valid, invalid };
  }, [options]);

  const getPreviewStructure = useCallback((supplier: SupplierInfo, product?: ProductInfo) => {
    return getSupplierFolderStructure(supplier, product);
  }, []);

  const uploadSingleImage = useCallback(async (
    file: File,
    supplier: SupplierInfo,
    product?: ProductInfo
  ): Promise<CloudinaryUploadResult> => {
    if (!configValid) {
      const errorMessage = `Cloudinary configuration invalid: ${configErrors.join(', ')}`;
      setError(errorMessage);
      options.onError?.(errorMessage);
      return {
        supplierName: supplier.name,
        imageUrl: '',
        uploadStatus: `Failed: ${errorMessage}`,
        error: errorMessage,
        uploadedAt: new Date().toISOString()
      };
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);
    setUploadedCount(0);
    setTotalCount(1);
    setCurrentFile(file.name);

    try {
      const result = await uploadImageToCloudinary(file, supplier, product, options.uploadOptions);
      
      if (result.uploadStatus === 'Success') {
        setUploadProgress(100);
        setUploadedCount(1);
        setResults([result]);
        options.onSuccess?.([result]);
      } else {
        setError(result.error || 'Upload failed');
        options.onError?.(result.error || 'Upload failed');
      }
      
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      options.onError?.(errorMessage);
      
      return {
        supplierName: supplier.name,
        imageUrl: '',
        uploadStatus: `Failed: ${errorMessage}`,
        error: errorMessage,
        uploadedAt: new Date().toISOString()
      };
    } finally {
      setIsUploading(false);
      setCurrentFile(null);
    }
  }, [options, configValid, configErrors]);

  const uploadMultipleImages = useCallback(async (
    files: File[],
    supplier: SupplierInfo,
    product?: ProductInfo
  ): Promise<CloudinaryUploadResult[]> => {
    if (!configValid) {
      const errorMessage = `Cloudinary configuration invalid: ${configErrors.join(', ')}`;
      setError(errorMessage);
      options.onError?.(errorMessage);
      return [];
    }

    // Validate files first
    const { valid: validFiles, invalid: invalidFiles } = validateFiles(files);
    
    if (validFiles.length === 0) {
      const errorMessage = 'No valid files to upload';
      setError(errorMessage);
      options.onError?.(errorMessage);
      return [];
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);
    setUploadedCount(0);
    setTotalCount(validFiles.length);
    setCurrentFile(null);

    try {
      const uploadResults = await uploadProductImages(validFiles, supplier, product, options.uploadOptions);
      
      const successfulUploads = uploadResults.filter(result => result.uploadStatus === 'Success');
      const failedUploads = uploadResults.filter(result => result.uploadStatus !== 'Success');
      
      setUploadProgress(100);
      setUploadedCount(successfulUploads.length);
      setResults(uploadResults);
      
      if (failedUploads.length > 0) {
        const errorMessage = `${failedUploads.length} out of ${validFiles.length} uploads failed`;
        setError(errorMessage);
        options.onError?.(errorMessage);
      } else {
        options.onSuccess?.(uploadResults);
      }
      
      return uploadResults;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      options.onError?.(errorMessage);
      return [];
    } finally {
      setIsUploading(false);
      setCurrentFile(null);
    }
  }, [options, configValid, configErrors, validateFiles]);

  const uploadWithProgress = useCallback(async (
    files: File[],
    supplier: SupplierInfo,
    product?: ProductInfo
  ): Promise<CloudinaryUploadResult[]> => {
    if (!configValid) {
      const errorMessage = `Cloudinary configuration invalid: ${configErrors.join(', ')}`;
      setError(errorMessage);
      options.onError?.(errorMessage);
      return [];
    }

    // Validate files first
    const { valid: validFiles, invalid: invalidFiles } = validateFiles(files);
    
    if (validFiles.length === 0) {
      const errorMessage = 'No valid files to upload';
      setError(errorMessage);
      options.onError?.(errorMessage);
      return [];
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);
    setUploadedCount(0);
    setTotalCount(validFiles.length);

    try {
      const uploadResults = await uploadImagesWithProgress(
        validFiles,
        supplier,
        product,
        (progress, completed, total, currentFileName) => {
          setUploadProgress(progress);
          setUploadedCount(completed);
          setTotalCount(total);
          setCurrentFile(currentFileName || null);
          options.onProgress?.(progress, completed, total, currentFileName);
        },
        options.uploadOptions
      );
      
      const successfulUploads = uploadResults.filter(result => result.uploadStatus === 'Success');
      const failedUploads = uploadResults.filter(result => result.uploadStatus !== 'Success');
      
      setResults(uploadResults);
      
      if (failedUploads.length > 0) {
        const errorMessage = `${failedUploads.length} out of ${validFiles.length} uploads failed`;
        setError(errorMessage);
        options.onError?.(errorMessage);
      } else {
        options.onSuccess?.(uploadResults);
      }
      
      return uploadResults;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      options.onError?.(errorMessage);
      return [];
    } finally {
      setIsUploading(false);
      setCurrentFile(null);
    }
  }, [options, configValid, configErrors, validateFiles]);

  return {
    uploadSingleImage,
    uploadMultipleImages,
    uploadWithProgress,
    validateFiles,
    getPreviewStructure,
    isUploading,
    uploadProgress,
    uploadedCount,
    totalCount,
    currentFile,
    error,
    results,
    configValid,
    configErrors,
    resetState,
  };
} 